/**
 * Core Mancala (Kalah) Game Types
 *
 * Board: 2 rows of 6 pits + 2 stores (mancalas).
 * Standard Kalah rules: sow counter-clockwise, capture, extra turns.
 */

// ============================================================================
// Board Configuration Constants
// ============================================================================

export const PITS_PER_SIDE = 6;
export const SEEDS_PER_PIT = 4;
export const TOTAL_SEEDS = 48;

// ============================================================================
// Core Types
// ============================================================================

/** Which player's turn / side */
export type PlayerSide = 'player1' | 'player2';

/**
 * Board state:
 * - pits[0] = Player 1's pits (bottom row, indices 0-5 left to right)
 * - pits[1] = Player 2's pits (top row, indices 0-5 right to left visually)
 * - stores[0] = Player 1's store (right side)
 * - stores[1] = Player 2's store (left side)
 */
export interface MancalaBoardState {
  pits: number[][];
  stores: number[];
}

/** A move: which pit index (0-5) the player picks up seeds from */
export interface MancalaMove {
  pitIndex: number;
  player: PlayerSide;
  timestamp: number;
}

/** Result of executing a move */
export interface MancalaMoveResult {
  /** Whether the player gets another turn */
  extraTurn: boolean;
  /** Whether a capture occurred */
  captured: boolean;
  /** Number of seeds captured (including the landing seed) */
  capturedSeeds: number;
  /** The pit index where the last seed landed (-1 for store) */
  lastLandingPit: number;
  /** Whether the last seed landed in the player's store */
  landedInStore: boolean;
  /** The updated board state */
  board: MancalaBoardState;
  /** The move that was executed */
  move: MancalaMove;
}

// ============================================================================
// Game State
// ============================================================================

export interface MancalaGameState {
  board: MancalaBoardState;
  currentPlayer: PlayerSide;
  moveHistory: MancalaMove[];
  gameStatus: 'active' | 'completed';
  winner?: PlayerSide | 'draw';
  score: { player1: number; player2: number };
  gameId: string;
  startedAt: number;
  lastMoveResult?: MancalaMoveResult;
}

// ============================================================================
// Game Settings
// ============================================================================

export type MancalaDifficulty = 'easy' | 'medium' | 'hard';

export interface MancalaGameSettings {
  mode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';
  aiDifficulty?: MancalaDifficulty;
  /** Which side the human plays in single player (default: player1) */
  playerSide?: PlayerSide;
}

// ============================================================================
// Statistics
// ============================================================================

export interface MancalaGameStats {
  wins: number;
  losses: number;
  draws: number;
  winPercentage: number;
  averageScore: number;
  highestScore: number;
  totalSeedsCaptured: number;
}

export interface MancalaStatistics {
  userId?: string;
  singlePlayer: {
    easy: MancalaGameStats;
    medium: MancalaGameStats;
    hard: MancalaGameStats;
  };
  localMultiplayer: MancalaGameStats;
  onlineMultiplayer: MancalaGameStats;
  totalGames: number;
  totalPlayTime: number;
}

export interface MancalaGameResult {
  gameId: string;
  mode: MancalaGameSettings['mode'];
  difficulty?: MancalaDifficulty;
  winner?: PlayerSide | 'draw';
  score: { player1: number; player2: number };
  totalMoves: number;
  totalCaptures: number;
  duration: number;
  completedAt: number;
}

// ============================================================================
// Component Props
// ============================================================================

export interface MancalaGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

export interface MancalaGameModeSelectorProps {
  onStartGame: (settings: MancalaGameSettings) => void;
  onJoinGame?: (roomCode: string) => Promise<void>;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
  isOnline?: boolean;
}

export interface MancalaBoardProps {
  board: MancalaBoardState;
  currentPlayer: PlayerSide;
  validPits: number[];
  lastMoveResult?: MancalaMoveResult;
  onPitClick: (pitIndex: number) => void;
  disabled?: boolean;
  animating?: boolean;
}
